import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Scales, ArrowRight, Gavel } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Verfahren() {
  const [akten, setAkten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ gericht: "", az_gericht: "", klage_eingereicht_am: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/verfahren");
      setAkten(r.data.akten || []);
    } catch {
      toast.error("Konnte Verfahren nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (a) => {
    setDialog(a);
    setForm({
      gericht: a.gericht || "",
      az_gericht: a.az_gericht || "",
      klage_eingereicht_am: a.klage_eingereicht_am || "",
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/akten/${dialog.id}`, form);
      toast.success("Verfahrensdaten gespeichert");
      setDialog(null);
      load();
    } catch {
      toast.error("Fehler beim Speichern");
    }
  };

  return (
    <div className="space-y-8" data-testid="verfahren-page">
      <header>
        <p className="overline text-[#D32F2F] mb-2">Workflow · 8</p>
        <h1 className="font-serif text-3xl lg:text-4xl">Verfahren</h1>
        <p className="text-sm text-[#6B7280] mt-2 max-w-2xl">
          Akten, in denen das vorgerichtliche Verfahren gescheitert ist und Klage erhoben wurde — mit Gericht, Aktenzeichen und Status.
        </p>
      </header>

      {loading ? (
        <p className="text-sm text-[#9CA3AF]">Lade …</p>
      ) : akten.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center">
          <Scales size={32} weight="duotone" className="text-[#1A2238] mx-auto mb-3" />
          <p className="font-serif text-xl mb-1">Keine Akten im Verfahren</p>
          <p className="text-sm text-[#6B7280]">
            Setzen Sie eine Akte auf <span className="font-mono">Verfahren</span>, sobald Klage eingereicht wurde — dann erscheint sie hier.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {akten.map(a => (
            <li key={a.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`verfahren-row-${a.id}`}>
              <div className="h-10 w-10 rounded-full bg-[#F3E8FF] text-[#6B21A8] flex items-center justify-center shrink-0">
                <Gavel size={18} weight="duotone" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <p className="font-medium">{a.aktenzeichen} · {a.mandant_name || "—"}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5">
                  {a.gericht ? `${a.gericht}` : "Gericht: ?"} {a.az_gericht && ` · Az: ${a.az_gericht}`}
                  {a.klage_eingereicht_am && ` · Klage eingereicht am ${new Date(a.klage_eingereicht_am).toLocaleDateString("de-DE")}`}
                </p>
              </div>
              <Button onClick={() => openEdit(a)} variant="outline" size="sm" data-testid={`verfahren-edit-${a.id}`}>
                Verfahrensdaten
              </Button>
              <Button asChild className="bg-[#1A2238] hover:bg-[#2C3859]" size="sm">
                <Link to={`/app/akten/${a.id}`}>Akte öffnen <ArrowRight size={14} className="ml-1" /></Link>
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Verfahrensdaten · {dialog?.aktenzeichen}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Gericht</Label>
              <Input value={form.gericht} onChange={(e) => setForm({ ...form, gericht: e.target.value })} placeholder="z.B. Amtsgericht Köln" data-testid="verfahren-gericht" />
            </div>
            <div>
              <Label>Az. (Gericht)</Label>
              <Input value={form.az_gericht} onChange={(e) => setForm({ ...form, az_gericht: e.target.value })} placeholder="z.B. 142 C 1234/26" data-testid="verfahren-az" />
            </div>
            <div>
              <Label>Klage eingereicht am</Label>
              <Input type="date" value={form.klage_eingereicht_am || ""} onChange={(e) => setForm({ ...form, klage_eingereicht_am: e.target.value })} data-testid="verfahren-klage-datum" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
              <Button type="submit" className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="verfahren-submit">Speichern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
